import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  Activity,
  Shield,
  Zap,
  BarChart3,
  CheckCircle2,
  Building2,
  Users,
  Stethoscope,
  Network,
  HeartPulse,
  ClipboardList
} from 'lucide-react';

const stats = [
  { value: 150, suffix: 'K+', label: 'Screenings' },
  { value: 500, suffix: '+', label: 'Clients' },
  { value: 98, suffix: '%', label: 'Satisfaction' },
  { value: 45, suffix: '', label: 'Mobile Units' },
];

const features = [
  {
    icon: Activity,
    title: 'Comprehensive Diagnostics',
    description: 'Full-spectrum health assessments with advanced biomarker analysis and real-time reporting.',
  },
  {
    icon: Zap,
    title: 'Rapid Results',
    description: 'Same-day insights powered by cutting-edge diagnostic technology and AI-assisted analysis.',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'HIPAA-compliant infrastructure with end-to-end encryption and SOC 2 certification.',
  },
  {
    icon: BarChart3,
    title: 'Data Intelligence',
    description: 'Actionable analytics and population health insights for informed decision-making.',
  },
];

const services = [
  {
    icon: HeartPulse,
    title: 'Preventative Screenings',
    description: 'Comprehensive health screenings including cardiovascular, diabetes, cancer detection, and wellness checkups delivered directly to your workplace.',
  },
  {
    icon: Network,
    title: 'Healthcare Provider Management',
    description: 'We manage and coordinate healthcare providers nationwide, ensuring quality care delivery, compliance, and operational excellence.',
  },
  {
    icon: Building2,
    title: 'Corporate Wellness Programs',
    description: 'Custom-designed wellness initiatives that improve employee health, reduce absenteeism, and boost productivity.',
  },
  {
    icon: ClipboardList,
    title: 'Compliance & Reporting',
    description: 'Full regulatory compliance management with detailed reporting, analytics, and audit support for healthcare organizations.',
  },
];

const benefits = [
  'Reduce healthcare costs by 30%',
  'Increase workforce productivity',
  'Early detection of chronic conditions',
  'Minimize employee absenteeism',
  'Comprehensive health analytics',
  'Provider network management',
];

export const Home = () => {
  return (
    <div className="bg-zinc-950 min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
        {/* Background Grid */}
        <div className="absolute inset-0 grid-pattern opacity-50" />
        
        {/* Gradient Orbs */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-[100px]" />

        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="overline mb-6 block">Preventative Health & Provider Management</span>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold text-white leading-[0.95] mb-8 font-['Outfit'] tracking-tighter">
                The future of<br />
                <span className="gradient-text">workplace health</span>
              </h1>
              <p className="text-lg md:text-xl text-zinc-400 mb-10 max-w-2xl leading-relaxed">
                Enterprise-grade health screening solutions and healthcare provider management services. 
                We identify health risks before they become problems and ensure quality care delivery across your organization.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/request-screening" data-testid="hero-cta-primary">
                  <motion.div whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }}>
                    <Button className="rounded-full px-8 py-6 text-base font-medium bg-emerald-500 hover:bg-emerald-400 text-black transition-all duration-200 glow-emerald">
                      Schedule Consultation
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                  </motion.div>
                </Link>
                <Link to="/services" data-testid="hero-cta-secondary">
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                    <Button variant="outline" className="rounded-full px-8 py-6 text-base font-medium border-white/20 text-white hover:bg-white/5 hover:border-white/30 transition-all duration-200">
                      Explore Services
                    </Button>
                  </motion.div>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 rounded-full border border-white/20 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/40 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-24 border-t border-white/5" data-testid="stats-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`stat-${index}`}
              >
                <p className="stat-display text-white mb-2">
                  <CountUp end={stat.value} duration={2.5} enableScrollSpy scrollSpyOnce />
                  <span className="text-emerald-500">{stat.suffix}</span>
                </p>
                <p className="text-zinc-500 text-sm tracking-wide uppercase">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-32 bg-zinc-900/30" data-testid="what-we-do-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="overline mb-4 block">What We Do</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] tracking-tighter mb-6">
              Comprehensive Healthcare Solutions
            </h2>
            <p className="text-zinc-400 text-lg leading-relaxed">
              From on-site health screenings to managing healthcare provider networks, 
              we deliver end-to-end solutions that improve outcomes and reduce costs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-surface rounded-2xl p-8 hover:border-emerald-500/30 border-glow group"
                data-testid={`service-home-${index}`}
              >
                <div className="w-14 h-14 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <service.icon className="w-7 h-7 text-emerald-500" />
                </div>
                <h3 className="text-2xl font-semibold text-white mb-4 font-['Outfit']">{service.title}</h3>
                <p className="text-zinc-400 leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 relative" data-testid="features-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-20"
          >
            <span className="overline mb-4 block">Capabilities</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white font-['Outfit'] tracking-tighter">
              Enterprise-ready<br />screening platform
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="card-surface rounded-2xl p-8 hover:border-emerald-500/30 border-glow"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-6">
                  <feature.icon className="w-6 h-6 text-emerald-500" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-3 font-['Outfit']">{feature.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Provider Management Section */}
      <section className="py-32 bg-zinc-900/30" data-testid="provider-management-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="overline mb-4 block">Healthcare Provider Management</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 font-['Outfit'] tracking-tighter">
                We manage healthcare providers nationwide
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                Beyond screenings, we provide comprehensive management services for healthcare providers. 
                Our platform ensures quality care delivery, regulatory compliance, and operational 
                efficiency across your provider network.
              </p>
              
              <div className="space-y-4">
                {[
                  'Provider credentialing and enrollment',
                  'Quality assurance and compliance monitoring',
                  'Revenue cycle management',
                  'Network optimization and analytics',
                  'Telehealth integration support',
                  'Patient care coordination',
                ].map((item, index) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-6 h-6 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    </div>
                    <span className="text-zinc-300">{item}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-emerald-500/10 to-transparent rounded-3xl p-10 border border-emerald-500/20">
                <Network className="w-14 h-14 text-emerald-500 mb-6" />
                <h3 className="text-3xl font-bold text-white mb-4 font-['Outfit']">
                  500+ Providers Managed
                </h3>
                <p className="text-zinc-400 mb-8">
                  Our network spans across all 50 states, ensuring quality healthcare 
                  delivery wherever your organization operates.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-500 font-['JetBrains_Mono']">50</p>
                    <p className="text-xs text-zinc-600 mt-1">States Covered</p>
                  </div>
                  <div className="bg-zinc-900/50 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-500 font-['JetBrains_Mono']">99.2%</p>
                    <p className="text-xs text-zinc-600 mt-1">Compliance Rate</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-32 border-t border-white/5" data-testid="benefits-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="overline mb-4 block">Why Choose Us</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 font-['Outfit'] tracking-tighter">
                Measurable impact on workforce health
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-10">
                Our data-driven approach delivers tangible ROI through early detection, 
                reduced healthcare costs, and improved employee wellbeing.
              </p>
              
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={benefit}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-center gap-4"
                    data-testid={`benefit-${index}`}
                  >
                    <div className="w-6 h-6 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                    </div>
                    <span className="text-zinc-300">{benefit}</span>
                  </motion.li>
                ))}
              </ul>

              <Link to="/request-screening" data-testid="benefits-cta">
                <motion.div whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }}>
                  <Button className="mt-10 rounded-full px-8 py-6 text-base font-medium bg-emerald-500 hover:bg-emerald-400 text-black transition-all duration-200">
                    Start Your Program
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </motion.div>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-square rounded-3xl overflow-hidden bg-zinc-900 border border-white/10">
                <img
                  src="https://images.pexels.com/photos/29833480/pexels-photo-29833480.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Abstract data visualization"
                  className="w-full h-full object-cover opacity-80"
                />
              </div>
              
              {/* Floating Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="absolute -bottom-6 -left-6 bg-zinc-900 border border-white/10 rounded-2xl p-6"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                    <BarChart3 className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white font-['JetBrains_Mono']">30%</p>
                    <p className="text-xs text-zinc-500">Avg. Cost Reduction</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 to-transparent" />
        <div className="container mx-auto px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Outfit'] tracking-tighter">
              Ready to transform your<br />workplace health?
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Join 500+ organizations that trust HealthCheck Systems for enterprise health screening 
              and healthcare provider management.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/request-screening" data-testid="cta-primary-btn">
                <motion.div whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }}>
                  <Button className="rounded-full px-8 py-6 text-base font-medium bg-emerald-500 hover:bg-emerald-400 text-black transition-all duration-200 glow-emerald">
                    Get Started
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </motion.div>
              </Link>
              <Link to="/contact" data-testid="cta-secondary-btn">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button variant="outline" className="rounded-full px-8 py-6 text-base font-medium border-white/20 text-white hover:bg-white/5 hover:border-white/30 transition-all duration-200">
                    Contact Sales
                  </Button>
                </motion.div>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 border-t border-white/5" data-testid="trust-section">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 text-center">
            {[
              { icon: Shield, label: 'HIPAA Compliant' },
              { icon: Building2, label: 'SOC 2 Certified' },
              { icon: Users, label: 'Fortune 500 Trusted' },
              { icon: Stethoscope, label: 'Joint Commission' },
            ].map((item, index) => (
              <motion.div 
                key={item.label} 
                className="flex items-center gap-3 text-zinc-500"
                whileHover={{ scale: 1.05, color: '#a1a1aa' }}
              >
                <item.icon className="w-5 h-5 text-emerald-500/50" />
                <span className="text-sm">{item.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
